import React, { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, HelpCircle, Music2, RefreshCw } from 'lucide-react'

const RoomHeader = ({
  roomCode,
  isConnected,
  currentSong = null,
  authoritativeIsPlaying = false,
  authoritativePosSeconds = 0,
  authoritativeAnchorPosSec = 0,
  authoritativeAnchorServerMs = 0,
  onOpenTutorial,
  onManualResync
}) => {
  const [nowMs, setNowMs] = useState(() => Date.now())
  const nowPlayingTitle = currentSong?.title || 'Nothing is playing yet'
  const nowPlayingArtist = currentSong?.artist || 'Queue up a song to get started'
  const nowPlayingThumb = currentSong?.youtubeId
    ? `/uploads/songs/${currentSong.youtubeId}/thumbnail.webp`
    : null
  const playingDurationSec = Number.isFinite(currentSong?.duration) ? currentSong.duration : null

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowMs(Date.now())
    }, 1000)

    return () => window.clearInterval(timer)
  }, [])

  const remainingLabel = useMemo(() => {
    if (!currentSong || !Number.isFinite(playingDurationSec)) {
      return null
    }

    const anchorBase = Number.isFinite(authoritativeAnchorPosSec)
      ? authoritativeAnchorPosSec
      : (Number.isFinite(authoritativePosSeconds) ? authoritativePosSeconds : 0)
    const anchorMs = Number.isFinite(authoritativeAnchorServerMs) && authoritativeAnchorServerMs > 0
      ? authoritativeAnchorServerMs
      : nowMs
    const elapsedSec = authoritativeIsPlaying ? Math.max(0, (nowMs - anchorMs) / 1000) : 0
    const currentPosSec = Math.max(0, anchorBase + elapsedSec)
    const remainingSec = Math.max(0, Math.ceil(playingDurationSec - currentPosSec))
    const minutes = Math.floor(remainingSec / 60)
    const seconds = remainingSec % 60
    const baseLabel = `${minutes}:${seconds.toString().padStart(2, '0')}`

    return authoritativeIsPlaying ? `${baseLabel} left` : `Paused ${baseLabel}`
  }, [
    authoritativeAnchorPosSec,
    authoritativePosSeconds,
    authoritativeAnchorServerMs,
    authoritativeIsPlaying,
    currentSong,
    nowMs,
    playingDurationSec
  ])

  return (
    <header className="room-safe-top">
      <div className="room-content-safe-inline mx-auto w-full max-w-lg pb-3 pt-3">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold tracking-tight text-[var(--room-text)]">
              Room {roomCode}
            </h1>
          </div>

          {typeof onOpenTutorial === 'function' ? (
            <button
              type="button"
              onClick={onOpenTutorial}
              className="room-focus-ring flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]"
              aria-label="Open tutorial"
              title="Open tutorial"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <section className="overflow-hidden rounded-2xl" data-tutorial-id="room-now-playing">
          <div
            className="room-glow relative overflow-hidden rounded-2xl border border-white/10 p-6"
            style={
              nowPlayingThumb
                ? {
                  backgroundImage: `linear-gradient(100deg, rgba(3,9,18,0.86), rgba(3,9,18,0.35)), url(${nowPlayingThumb})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }
                : {
                  backgroundImage: 'linear-gradient(140deg, #081a2f 0%, #0a2442 100%)'
                }
            }
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 bg-[color:rgba(46,168,255,0.2)] text-[var(--room-text)]">
                  <Music2 className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--room-muted)]">
                    Now Playing
                  </p>
                  <p className="truncate text-sm font-semibold text-[var(--room-text)]">{nowPlayingTitle}</p>
                  <p className="truncate text-xs text-[var(--room-muted)]">{nowPlayingArtist}</p>
                </div>
              </div>
              {remainingLabel ? (
                <div className="shrink-0 rounded-full border border-[color:rgba(46,168,255,0.45)] bg-[color:rgba(46,168,255,0.16)] px-3 py-1 text-xs font-semibold text-[var(--room-primary)]">
                  {remainingLabel}
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {!isConnected ? (
          <div
            className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-[color:rgba(255,102,122,0.35)] bg-[color:rgba(255,102,122,0.12)] px-3 py-2 text-xs text-[var(--room-text)]"
            role="status"
            aria-live="polite"
          >
            <span className="inline-flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[var(--room-danger)]" />
              <span>Connection issue detected. Realtime sync may be delayed.</span>
            </span>
            {typeof onManualResync === 'function' ? (
              <button
                type="button"
                onClick={onManualResync}
                className="room-focus-ring inline-flex h-9 items-center gap-1 rounded-lg border border-white/20 bg-white/10 px-2 text-xs font-semibold text-[var(--room-text)] hover:bg-white/20"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Resync
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  )
}

export default RoomHeader
